import type { ReactNode } from "react";
import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import type { Omittable } from "../../lib/props";

/** Chart margin (space reserved for axes/labels around the plotting area). */
export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const DEFAULT_MARGIN: ChartMargin = { top: 8, right: 8, bottom: 8, left: 8 };

export interface ChartFrameRenderProps {
  /** Full svg width. */
  width: number;
  /** Full svg height. */
  height: number;
  /** Plotting-area width (`width` minus horizontal margin). */
  innerWidth: number;
  /** Plotting-area height (`height` minus vertical margin). */
  innerHeight: number;
  /** The resolved margin. */
  margin: ChartMargin;
}

export type ChartFrameProps = Omittable<{
  /** Space reserved around the plotting area for axes/labels. */
  margin?: Partial<ChartMargin>;
  /** Fixed height in px. Omit to fill the parent (parent must be sized). */
  height?: number;
  /** aria-label for the chart svg. */
  ariaLabel?: string;
  /**
   * Render your marks against the measured geometry. The origin is already
   * translated by `margin` (a `<Group>`), so draw in `[0, innerWidth]` ×
   * `[0, innerHeight]` space.
   */
  children: (geometry: ChartFrameRenderProps) => ReactNode;
}>;

/**
 * The base for custom charts: a self-measuring `<svg>` with a margin-translated
 * `<Group>`, exposing `{ width, height, innerWidth, innerHeight, margin }` to a
 * render-prop child. Compose scales + marks (`scaleBand`, `Bar`, `AxisBottom`, …)
 * inside it. The XYChart scaffold is the easy path; reach for `ChartFrame` when
 * you need a layout visx's XYChart doesn't model (hierarchy, flow, geo, heatmap).
 */
export function ChartFrame({
  margin: marginProp,
  height,
  ariaLabel,
  children,
}: ChartFrameProps) {
  const margin: ChartMargin = { ...DEFAULT_MARGIN, ...marginProp };
  return (
    <ParentSize
      style={height != null ? { height, width: "100%" } : { width: "100%", height: "100%" }}
    >
      {({ width, height: measuredHeight }) => {
        const h = height ?? measuredHeight;
        if (width <= 0 || h <= 0) return null;
        const innerWidth = Math.max(0, width - margin.left - margin.right);
        const innerHeight = Math.max(0, h - margin.top - margin.bottom);
        return (
          <svg width={width} height={h} aria-label={ariaLabel} role="img">
            <Group left={margin.left} top={margin.top}>
              {children({ width, height: h, innerWidth, innerHeight, margin })}
            </Group>
          </svg>
        );
      }}
    </ParentSize>
  );
}
